import { Copy, ThumbsDown, ThumbsUp, RotateCcw } from "lucide-react";
import MarkdownAccordion from "../MarkdownAccordion";
import LogoSvg from "../../assets/svg/logo.svg";
import LogoDark from "../../assets/svg/logo_dark.svg";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  attachments?: string[];
  error?: boolean;
}

interface ChatMessageProps {
  message: Message;
  feedbackStatus: Map<string, "like" | "dislike">;
  copyStatus: Map<string, boolean>;
  onFeedback: (messageId: string, feedback: "like" | "dislike") => void;
  onCopy: (messageId: string, content: string) => void;
  onRetry: (messageId: string) => void;
}

const AssistantBadge = () => (
  <div className="h-8 w-8 overflow-hidden rounded-full">
    <img
      src={LogoSvg}
      alt="Assistant"
      className="h-full w-full object-cover dark:hidden"
    />
    <img
      src={LogoDark}
      alt="Assistant"
      className="h-full w-full object-cover hidden dark:block"
    />
  </div>
);

export function ChatMessage({
  message,
  feedbackStatus,
  copyStatus,
  onFeedback,
  onCopy,
  onRetry,
}: ChatMessageProps) {
  if (message.role === "user") {
    return (
      <div className="flex w-full justify-end px-4 py-2">
        <div className="flex max-w-[70%] flex-col items-end gap-2">
          <div
            className="rounded-2xl px-4 py-2.5 shadow-sm bg-[#1A2D47] dark:bg-[#2C405F] text-[#FCFBFA] dark:text-[#E3E6EA]"
            style={{
              fontFamily: "Sofia Pro, sans-serif",
              fontWeight: 300,
              fontSize: "16px",
            }}
          >
            {message.content}
          </div>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex w-full justify-start px-4 py-4">
      <div className="flex w-full flex-col gap-3">
        <div className="flex-shrink-0">
          <AssistantBadge />
        </div>
        <div className="flex w-full flex-col gap-3">
          <div
            className="prose prose-sm max-w-none w-full text-[#1D1B19] dark:text-[#E3E6EA]"
            style={{
              fontFamily: "Sofia Pro, sans-serif",
              fontWeight: 300,
              fontSize: "16px",
            }}
          >
            <MarkdownAccordion source={message.content} />
          </div>

          {!message.error && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onCopy(message.id, message.content)}
                className="rounded-lg p-2 text-[#8E8378] transition hover:bg-[#F5EFE6] hover:text-[#2C405F]"
                title="Copy"
              >
                <Copy size={16} />
              </button>
              {copyStatus.get(message.id) && (
                <span className="text-xs text-[#2C405F] dark:text-[#9AAABF]">
                  Copied!
                </span>
              )}
              <div className="h-4 w-px bg-[#E5DDD3] dark:bg-[#455D7D]" />
              <button
                type="button"
                onClick={() => onFeedback(message.id, "like")}
                className={`rounded-lg p-2 transition ${
                  feedbackStatus.get(message.id) === "like"
                    ? "bg-[#2C405F] text-white"
                    : "text-[#8E8378] hover:bg-[#F5EFE6] hover:text-[#2C405F]"
                }`}
                title="Like"
              >
                <ThumbsUp size={16} />
              </button>
              <button
                type="button"
                onClick={() => onFeedback(message.id, "dislike")}
                className={`rounded-lg p-2 transition ${
                  feedbackStatus.get(message.id) === "dislike"
                    ? "bg-[#2C405F] text-white"
                    : "text-[#8E8378] hover:bg-[#F5EFE6] hover:text-[#2C405F]"
                }`}
                title="Dislike"
              >
                <ThumbsDown size={16} />
              </button>
              <div className="h-4 w-px bg-[#E5DDD3]" />
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className="rounded-lg p-2 text-[#8E8378] transition hover:bg-[#F5EFE6] hover:text-[#2C405F]"
                title="Retry"
              >
                <RotateCcw size={16} />
              </button>
            </div>
          )}

          {message.error && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600">
                Failed to send message
              </span>
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className="inline-flex items-center gap-1 rounded-lg bg-[#2C405F] px-3 py-1.5 text-sm text-white transition hover:bg-[#1A2D47]"
              >
                <RotateCcw size={14} />
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
